/// <amd-module name="Dashboards/WidgetHost/Helpers/PresentationContextHelpers" />
import { DashboardWidgetProps, WidgetGridSize } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { WidgetHostProps } from "Dashboards/WidgetContracts/Internal/WidgetHostProps";
import {
    LoadedEventInfo,
    WidgetConfigurationProps,
    WidgetContextMenuColorMode,
    WidgetPresentationMode,
    WidgetProps,
    WidgetSize
} from "Dashboards/WidgetContracts/WidgetProps";
import { IVssContextualMenuItem } from "VSSUI/Components/VssContextualMenu";

/**
 * Callbacks on Widget Runtime Props for use by the contained Widget, which WidgetHost must honor.
 */
export interface IWidgetHostCallbacks {
    onWidgetLoaded(loadedEventInfo?: LoadedEventInfo): void;
    setMenuColorMode(widgetMenuColorMode: WidgetContextMenuColorMode): void;
    onConfigure(): void;
    setCustomContextMenuItems(customMenuItems: IVssContextualMenuItem[]): void;
}

/**
 * Assemble Widget configuration props together with runtime properties
 */
export function generateWidgetProps(
    presentationMode: WidgetPresentationMode,
    widgetHostProps: WidgetHostProps,
    widgetHostCallbacks: IWidgetHostCallbacks,
    sizeConverter: (size: WidgetGridSize) => WidgetSize
): WidgetProps {
    // Use caller-suplied settings, or fall back to mode based defaults.
    const showLoadAnimations =
        widgetHostProps.showLoadAnimations != null
            ? widgetHostProps.showLoadAnimations
            : presentationMode !== WidgetPresentationMode.dashboardConfigPreview && presentationMode !== WidgetPresentationMode.dashboardEditView;
    const avoidShowingModalUI =
        widgetHostProps.avoidShowingModalUI != null ? widgetHostProps.avoidShowingModalUI : presentationMode === WidgetPresentationMode.lightboxView;

    return {
        ...convertToWidgetProps(presentationMode, widgetHostProps.widgetProps, sizeConverter),
        presentationMode: presentationMode,
        showLoadAnimations: showLoadAnimations,
        shouldNotShowTitle: widgetHostProps.shouldNotShowTitle,
        openLinksInNewTab: widgetHostProps.openLinksInNewTab === true, // Widget links should use a new tab in certain non-browser "embedded" experiences.
        avoidShowingModalUI: avoidShowingModalUI,

        onWidgetLoaded: widgetHostCallbacks.onWidgetLoaded,
        onConfigure: widgetHostCallbacks.onConfigure,
        setMenuColorMode: widgetHostCallbacks.setMenuColorMode,
        setCustomContextMenuItems: widgetHostCallbacks.setCustomContextMenuItems,
        teamContext: widgetHostProps.teamContext
    };
}

export function convertToWidgetProps(
    presentationMode: WidgetPresentationMode,
    source: DashboardWidgetProps,
    sizeConverter: (size: WidgetGridSize) => WidgetSize
): WidgetConfigurationProps {
    const size = sizeConverter(source.size);

    return {
        ...source,
        size: size
    };
}

export function getSize(
    presentationMode: WidgetPresentationMode,
    source: DashboardWidgetProps,
    sizeConverter: (size: WidgetGridSize) => WidgetSize
): WidgetSize {
    return presentationMode === WidgetPresentationMode.lightboxView ? getLightboxSize() : sizeConverter(source.size);
}

/**
 * Calculates lightbox size as 85% of window size.
 */
export function getLightboxSize(): WidgetSize {
    return {
        width: window.innerWidth * 0.85,
        height: window.innerHeight * 0.85
    };
}

/**
 * Calculates a safe legacy grid-aware size for a lightbox
 */
export function getLegacyLightboxGridSize(): WidgetGridSize {
    const maxSize = getLightboxSize();
    // Determine max number of cells we could accomodate using our lightbox area, after accounting for +10 of gutter space and -20 of outer lightbox padding
    const colCells = Math.floor((maxSize.width - 20) / 170);
    const rowCells = Math.floor((maxSize.height - 20) / 170);
    return {
        columnSpan: colCells,
        rowSpan: rowCells
    };
}
